import type { TargetFile } from './targetFile.js';
import type { AppSiteMetadata, TemplateInfo } from './appSiteMetadata.js';
import type { ResourceStatistics } from '../../../Assembler/dist/model/index.js';

/**
 * Stores loaded template data (immutable after Load) for the PreProcessJson engine.
 * Separates loaded data from per-merge mutable state in the loader.
 */
export class PreProcessTemplateStoreJson {
    // Cache: AppSite -> Map<templateName, TargetFile>
    // Exposed as public properties for direct access by the loader
    readonly htmlJsonCache: Map<string, Map<string, TargetFile>> = new Map();
    readonly dataJsonCache: Map<string, Map<string, TargetFile>> = new Map();
    readonly metadataCache: Map<string, AppSiteMetadata> = new Map();
    readonly componentIndexCache: Map<string, TemplateInfo> = new Map();
    readonly resourceStatsCache: Map<string, ResourceStatistics> = new Map();

    // #region HtmlJson

    hasHtmlJsonAppSite(appSite: string): boolean {
        const htmlTemplates = this.htmlJsonCache.get(appSite);
        return htmlTemplates !== undefined;
    }

    tryGetHtmlJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const htmlTemplates = this.htmlJsonCache.get(appSite);
        if (htmlTemplates) {
            return htmlTemplates;
        }

        return undefined;
    }

    getHtmlJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const htmlTemplates = this.htmlJsonCache.get(appSite);
        if (htmlTemplates) {
            return htmlTemplates;
        }

        return undefined;
    }

    setHtmlJsonTemplates(appSite: string, templates: Map<string, TargetFile>): void {
        this.htmlJsonCache.set(appSite, templates);
    }

    getHtmlJsonAppSites(): IterableIterator<string> {
        const appSites = this.htmlJsonCache.keys();
        return appSites;
    }

    getHtmlJsonCount(appSite: string): number {
        const templates = this.htmlJsonCache.get(appSite);
        return templates ? templates.size : 0;
    }

    // #endregion

    // #region DataJson

    hasDataJsonAppSite(appSite: string): boolean {
        const dataTemplates = this.dataJsonCache.get(appSite);
        return dataTemplates !== undefined;
    }

    tryGetDataJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const dataTemplates = this.dataJsonCache.get(appSite);
        if (dataTemplates) {
            return dataTemplates;
        }

        return undefined;
    }

    getDataJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const dataTemplates = this.dataJsonCache.get(appSite);
        if (dataTemplates) {
            return dataTemplates;
        }

        return undefined;
    }

    setDataJsonTemplates(appSite: string, templates: Map<string, TargetFile>): void {
        this.dataJsonCache.set(appSite, templates);
    }

    getDataJsonAppSites(): IterableIterator<string> {
        const appSites = this.dataJsonCache.keys();
        return appSites;
    }

    getDataJsonCount(appSite: string): number {
        const templates = this.dataJsonCache.get(appSite);
        return templates ? templates.size : 0;
    }

    // #endregion

    // #region Metadata

    tryGetMetadata(key: string): AppSiteMetadata | undefined {
        const metadata = this.metadataCache.get(key);
        if (metadata) {
            return metadata;
        }

        return undefined;
    }

    getMetadata(key: string): AppSiteMetadata | undefined {
        const metadata = this.metadataCache.get(key);
        if (metadata) {
            return metadata;
        }

        return undefined;
    }

    setMetadata(key: string, metadata: AppSiteMetadata): void {
        this.metadataCache.set(key, metadata);
    }

    getMetadataKeys(): IterableIterator<string> {
        const metadataKeys = this.metadataCache.keys();
        return metadataKeys;
    }

    getAllMetadata(): Map<string, AppSiteMetadata> {
        const allMetadata = this.metadataCache;
        return allMetadata;
    }

    // #endregion

    // #region ComponentIndex

    // C# keys `_componentIndex` with StringComparer.OrdinalIgnoreCase; a JS Map compares keys
    // exactly, so the fold lives here -- in the store that owns the comparison -- rather than in
    // every caller.
    private static componentIndexKey(key: string): string {
        return key.toLowerCase();
    }

    hasComponentInfo(key: string): boolean {
        return this.componentIndexCache.has(PreProcessTemplateStoreJson.componentIndexKey(key));
    }

    clearComponentIndex(): void {
        this.componentIndexCache.clear();
    }

    getComponentIndexCount(): number {
        return this.componentIndexCache.size;
    }

    tryGetComponentInfo(key: string): TemplateInfo | undefined {
        return this.componentIndexCache.get(PreProcessTemplateStoreJson.componentIndexKey(key));
    }

    setComponentInfo(key: string, info: TemplateInfo): void {
        this.componentIndexCache.set(PreProcessTemplateStoreJson.componentIndexKey(key), info);
    }

    // #endregion

    // #region ResourceStats

    tryGetResourceStats(appSite: string): ResourceStatistics | undefined {
        const resourceStats = this.resourceStatsCache.get(appSite);
        if (resourceStats) {
            return resourceStats;
        }

        return undefined;
    }

    setResourceStats(appSite: string, stats: ResourceStatistics): void {
        this.resourceStatsCache.set(appSite, stats);
    }

    // #endregion

    clear(): void {
        this.htmlJsonCache.clear();
        this.dataJsonCache.clear();
        this.metadataCache.clear();
        this.componentIndexCache.clear();
        this.resourceStatsCache.clear();
    }
}
